import { useEffect, useCallback } from 'react';

interface UseKeyboardShortcutOptions {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean; // Cmd key on Mac
  shiftKey?: boolean;
  altKey?: boolean;
  preventDefault?: boolean;
}

/**
 * Custom hook for keyboard shortcuts
 * Supports Ctrl/Cmd key combinations
 * 
 * @example
 * useKeyboardShortcut({ key: 'k', metaKey: true }, () => console.log('Cmd+K pressed'));
 */
export function useKeyboardShortcut(
  options: UseKeyboardShortcutOptions,
  callback: (event: KeyboardEvent) => void,
  enabled: boolean = true
) {
  const {
    key,
    ctrlKey = false,
    metaKey = false,
    shiftKey = false,
    altKey = false,
    preventDefault = true,
  } = options;

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      const eventKey = typeof event.key === 'string' ? event.key : '';
      const wantedKey = typeof key === 'string' ? key : '';
      if (!eventKey || !wantedKey) return;

      const isKeyMatch = eventKey.toLowerCase() === wantedKey.toLowerCase();
      
      // Check if modifier keys match
      const isCtrlMatch = ctrlKey ? event.ctrlKey : true;
      const isMetaMatch = metaKey ? event.metaKey : true;
      const isShiftMatch = shiftKey ? event.shiftKey : true;
      const isAltMatch = altKey ? event.altKey : true;

      // Check if we should ignore the shortcut (e.g., user is typing in an input)
      const target = event.target as HTMLElement;
      const isInputField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;

      // If user is typing in an input field, ignore the shortcut unless explicitly allowed
      // Exception: Allow the shortcut if it's a global one (like Cmd+K for search)
      const shouldIgnore = isInputField && !(metaKey || ctrlKey);

      // Ignore bare letter keys when an unused modifier is held (avoids stray matches)
      const extraModifier =
        (!ctrlKey && event.ctrlKey) ||
        (!metaKey && event.metaKey) ||
        (!shiftKey && event.shiftKey) ||
        (!altKey && event.altKey);

      if (extraModifier && !(metaKey || ctrlKey)) {
        return;
      }

      if (
        isKeyMatch &&
        (!ctrlKey || isCtrlMatch) &&
        (!metaKey || isMetaMatch) &&
        (!shiftKey || isShiftMatch) &&
        (!altKey || isAltMatch) &&
        !shouldIgnore &&
        enabled
      ) {
        if (preventDefault) {
          event.preventDefault();
        }
        callback(event);
      }
    },
    [key, ctrlKey, metaKey, shiftKey, altKey, preventDefault, callback, enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress, enabled]);
}

export default useKeyboardShortcut;
